package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.colaboradores.DatasColab
import br.com.htms.inspeccaocampo.data.efetivo.Datas
import br.com.htms.inspeccaocampo.data.efetivo.EfetivoDados
import br.com.htms.inspeccaocampo.data.efetivo.FolgaStatus
import br.com.htms.inspeccaocampo.data.efetivo.RegrasEfetivo
import br.com.htms.inspeccaocampo.data.efetivo.TiposDia
import br.com.htms.inspeccaocampo.data.efetivo.Transferencia
import br.com.htms.inspeccaocampo.platform.currentTimeMillis

/** Modelo "Controle do efetivo": Efetivo, Obras, Obra x Função, Transferências, Dias lançados e Dashboard. */
object PlanilhaEfetivo {
    private fun rotuloFolga(s: String): String = when (s) {
        FolgaStatus.EM_DIA -> "Em dia"
        FolgaStatus.PROXIMA -> "Próxima"
        FolgaStatus.ADIADA -> "Adiada (fim de ano)"
        FolgaStatus.ATRASADA -> "Atrasada"
        else -> "Sem data"
    }

    private fun rotuloStatus(s: String): String = s.replace('_', ' ').replaceFirstChar { it.uppercase() }

    fun build(d: EfetivoDados): ByteArray {
        val hoje = d.hoje.ifBlank { DatasColab.isoDeDias(DatasColab.hoje()) }
        val agora = currentTimeMillis()
        val funcs = d.funcionarios.sortedBy { it.nome.lowercase() }

        data class LinhaF(
            val ativo: Boolean, val obra: String, val obraNome: String, val situacao: String,
            val folga: FolgaStatus?
        )
        val info = funcs.associate { f ->
            val ativo = f.noQuadro(hoje)
            val cc = RegrasEfetivo.obraEm(f.aloc, hoje)
            f.id to LinhaF(
                ativo,
                if (cc == null || cc == RegrasEfetivo.SEM_OBRA) "Sem obra" else d.codigo(cc),
                if (cc == null || cc == RegrasEfetivo.SEM_OBRA) "" else d.centro(cc)?.nome.orEmpty(),
                if (ativo) TiposDia.rotulo(RegrasEfetivo.tipoDia(f, hoje)) else "Desligado",
                if (ativo) RegrasEfetivo.folgaStatus(f, hoje) else null
            )
        }

        val colsEf = listOf(
            ColPlan("MATRÍCULA", 11), ColPlan("NOME", 38), ColPlan("FUNÇÃO", 30), ColPlan("VÍNCULO", 10),
            ColPlan("SITUAÇÃO CADASTRO", 16), ColPlan("ADMISSÃO", 12, TipoCol.DATA), ColPlan("DESLIGAMENTO", 13, TipoCol.DATA),
            ColPlan("REGIME (DIAS)", 10, TipoCol.NUMERO), ColPlan("ATIVO HOJE", 10), ColPlan("OBRA HOJE", 14),
            ColPlan("NOME DA OBRA", 32), ColPlan("SITUAÇÃO HOJE", 18), ColPlan("ÚLTIMA FOLGA", 12, TipoCol.DATA),
            ColPlan("DIAS DESDE A FOLGA", 12, TipoCol.NUMERO), ColPlan("LIMITE DA FOLGA", 12, TipoCol.DATA), ColPlan("STATUS DA FOLGA", 18)
        )
        val efetivo = PlanKit.abaDados(
            "EFETIVO", "Efetivo · situação em ${Datas.br(hoje)}",
            "Um funcionário por linha. Filtre por obra, função, vínculo ou situação: o DASHBOARD acompanha o filtro.",
            colsEf,
            funcs.map { f ->
                val i = info.getValue(f.id)
                listOf(
                    f.matricula.toIntOrNull() ?: f.matricula, f.nome, f.funcaoOuSem, f.vinculo, f.situacao,
                    PlanData.de(f.admissao), PlanData.de(f.desligamento), f.regime.takeIf { it > 0 },
                    i.ativo, i.obra, i.obraNome, i.situacao,
                    PlanData.de(i.folga?.ultima), i.folga?.takeIf { it.status != FolgaStatus.SEM_DATA }?.dias,
                    PlanData.de(i.folga?.limite), i.folga?.let { rotuloFolga(it.status) }.orEmpty()
                )
            },
            cor = "1565C0"
        )
        val obraEf = efetivo.faixa("OBRA HOJE")
        val ativoEf = efetivo.faixa("ATIVO HOJE")
        val funcEf = efetivo.faixa("FUNÇÃO")
        val ativosHoje = funcs.filter { info.getValue(it.id).ativo }

        val previstoPorObra = d.previsto.groupBy { d.codigo(it.cc) }.mapValues { (_, v) -> v.sumOf { it.qtd } }
        val transfAbertas = d.transferencias.filter { it.aberta }
        val colsObras = listOf(
            ColPlan("CÓDIGO", 14), ColPlan("OBRA", 36), ColPlan("ATIVA", 8), ColPlan("ENGENHEIRO", 28),
            ColPlan("EFETIVO ATUAL", 12, TipoCol.NUMERO), ColPlan("PREVISTO", 11, TipoCol.NUMERO),
            ColPlan("SALDO (ATUAL - PREVISTO)", 14, TipoCol.NUMERO), ColPlan("TRANSF. ABERTAS", 12, TipoCol.NUMERO)
        )
        val atualPorObra = ativosHoje.groupingBy { info.getValue(it.id).obra }.eachCount()
        val centros = d.centros.sortedWith(compareBy({ !it.ativo }, { it.codigo }))
        val obras = PlanKit.abaDados(
            "OBRAS", "Obras · efetivo atual x previsto", "Efetivo atual = funcionários ativos alocados hoje (aba EFETIVO).",
            colsObras,
            centros.map { c ->
                val atual = atualPorObra[c.codigo] ?: 0
                val prev = previstoPorObra[c.codigo] ?: 0
                listOf(
                    c.codigo, c.nome, c.ativo, c.engenheiroNome,
                    Fx("COUNTIFS($obraEf,A{r},$ativoEf,\"Sim\")", atual),
                    prev,
                    Fx("E{r}-F{r}", atual - prev),
                    transfAbertas.count { it.origem == c.id || it.destino == c.id }
                )
            },
            cor = "2E7D32"
        )

        val pares = (d.previsto.map { d.codigo(it.cc) to it.funcao } +
            ativosHoje.map { info.getValue(it.id).obra to it.funcaoOuSem }).distinct()
            .sortedWith(compareBy({ it.first }, { it.second }))
        val prevPar = d.previsto.groupBy { d.codigo(it.cc) to it.funcao }.mapValues { (_, v) -> v.sumOf { it.qtd } }
        val atualPar = ativosHoje.groupingBy { info.getValue(it.id).obra to it.funcaoOuSem }.eachCount()
        val obraFuncao = PlanKit.abaDados(
            "OBRA X FUNÇÃO", "Obra x Função · previsto x atual", "Atual conta os funcionários ativos hoje em cada obra e função.",
            listOf(
                ColPlan("OBRA", 14), ColPlan("FUNÇÃO", 34), ColPlan("PREVISTO", 11, TipoCol.NUMERO),
                ColPlan("ATUAL", 11, TipoCol.NUMERO), ColPlan("SALDO", 11, TipoCol.NUMERO)
            ),
            pares.map { (o, fn) ->
                val prev = prevPar[o to fn] ?: 0
                val atual = atualPar[o to fn] ?: 0
                listOf(
                    o, fn, prev,
                    Fx("COUNTIFS($obraEf,A{r},$funcEf,B{r},$ativoEf,\"Sim\")", atual),
                    Fx("D{r}-C{r}", atual - prev)
                )
            },
            cor = "00897B"
        )

        fun lado(t: Transferencia, chave: String): String =
            t.lado(chave).let { l -> listOf(rotuloStatus(l.dec), l.porNome).filter { it.isNotBlank() }.joinToString(" · ") }
        val transf = PlanKit.abaDados(
            "TRANSFERÊNCIAS", "Transferências entre obras", "Status, aprovação de cada lado e prazo.",
            listOf(
                ColPlan("CRIADA EM", 12, TipoCol.DATA), ColPlan("FUNCIONÁRIO", 36), ColPlan("FUNÇÃO", 26),
                ColPlan("ORIGEM", 14), ColPlan("DESTINO", 14), ColPlan("INÍCIO", 12, TipoCol.DATA), ColPlan("FIM", 12, TipoCol.DATA),
                ColPlan("STATUS", 14), ColPlan("VENCIDA", 9), ColPlan("APROVAÇÃO ORIGEM", 26), ColPlan("APROVAÇÃO DESTINO", 26),
                ColPlan("SOLICITANTE", 26), ColPlan("MOTIVO", 40)
            ),
            d.transferencias.sortedByDescending { it.criada }.map { t ->
                val f = d.func(t.fid)
                listOf(
                    PlanData.deMs(t.criada), f?.nome.orEmpty(), f?.funcaoOuSem.orEmpty(), d.codigo(t.origem), d.codigo(t.destino),
                    PlanData.de(t.ini), PlanData.de(t.fim), rotuloStatus(t.status), t.vencida(agora),
                    lado(t, "origem"), lado(t, "destino"), t.solicitanteNome, t.motivo
                )
            },
            cor = "6A1B9A"
        )

        val desde = Datas.add(hoje, -365)
        data class Lanc(val data: String, val f: String, val funcao: String, val tipo: String, val obra: String)
        val lancs = funcs.flatMap { f ->
            f.dias.filter { (dt, t) -> dt >= desde && t != TiposDia.TRABALHO }.map { (dt, t) ->
                val cc = RegrasEfetivo.obraEm(f.aloc, dt)
                Lanc(dt, f.nome, f.funcaoOuSem, TiposDia.rotulo(t), if (cc == null || cc == RegrasEfetivo.SEM_OBRA) "Sem obra" else d.codigo(cc))
            }
        }.sortedWith(compareByDescending<Lanc> { it.data }.thenBy { it.f })
        val dias = PlanKit.abaDados(
            "DIAS LANÇADOS", "Dias lançados (últimos 12 meses)", "Folgas, descansos, férias, afastamentos, faltas e feriados lançados no portal.",
            listOf(
                ColPlan("DATA", 12, TipoCol.DATA), ColPlan("MÊS", 9), ColPlan("FUNCIONÁRIO", 36), ColPlan("FUNÇÃO", 28),
                ColPlan("OBRA", 14), ColPlan("TIPO", 18)
            ),
            lancs.map { l -> listOf(PlanData.de(l.data), l.data.take(7), l.f, l.funcao, l.obra, l.tipo) },
            cor = "EF6C00"
        )

        val visEf = efetivo.visiveis()
        val sitEf = efetivo.faixa("SITUAÇÃO HOJE")
        fun ativoE(cond: String) = "SUMPRODUCT(($ativoEf=\"Sim\")*($cond)*$visEf)"
        fun sit(r: String) = ativosHoje.count { info.getValue(it.id).situacao == r }.toDouble()
        val atrasadas = ativosHoje.count { info.getValue(it.id).folga?.status == FolgaStatus.ATRASADA }
        val kpis = listOf(
            KpiPlan("Efetivo ativo hoje", efetivo.contarSe("ATIVO HOJE", "Sim"), ativosHoje.size.toDouble()),
            KpiPlan("Trabalhando hoje", ativoE("$sitEf=\"Trabalho\""), sit("Trabalho"), SharedOoxmlWriter.STYLE_CRIT_BAIXA),
            KpiPlan(
                "Folga / descanso", ativoE("($sitEf=\"Folga\")+($sitEf=\"Descanso semanal\")+($sitEf=\"Feriado\")"),
                sit("Folga") + sit("Descanso semanal") + sit("Feriado")
            ),
            KpiPlan("Férias", ativoE("$sitEf=\"Férias\""), sit("Férias")),
            KpiPlan("Afastados", ativoE("$sitEf=\"Afastamento\""), sit("Afastamento"), SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan("Faltas hoje", ativoE("$sitEf=\"Falta\""), sit("Falta"), SharedOoxmlWriter.STYLE_CRIT_ALTA),
            KpiPlan("Folgas atrasadas", efetivo.contarSe("STATUS DA FOLGA", "Atrasada"), atrasadas.toDouble(), SharedOoxmlWriter.STYLE_CRIT_ALTA),
            KpiPlan("Sem obra hoje", ativoE("$obraEf=\"Sem obra\""), ativosHoje.count { info.getValue(it.id).obra == "Sem obra" }.toDouble()),
            KpiPlan("Previsto (obras)", "SUM(${obras.faixa("PREVISTO")})", d.previsto.sumOf { it.qtd }.toDouble()),
            KpiPlan("Saldo x previsto", "SUM(${obras.faixa("SALDO (ATUAL - PREVISTO)")})",
                centros.sumOf { (atualPorObra[it.codigo] ?: 0) - (previstoPorObra[it.codigo] ?: 0) }.toDouble()),
            KpiPlan("Transferências abertas", "COUNTIF(${transf.faixa("STATUS")},\"Pendente\")+COUNTIF(${transf.faixa("STATUS")},\"Parcial\")",
                transfAbertas.size.toDouble(), SharedOoxmlWriter.STYLE_CRIT_MEDIA)
        )
        val totalAtivos = efetivo.contarSe("ATIVO HOJE", "Sim")
        val blocos = listOf(
            BlocoPlan("Efetivo ativo por obra", "Obra", PlanKit.contagem(ativosHoje) { info.getValue(it.id).obra },
                { ativoE("$obraEf=${PlanKit.texto(it)}") }, formulaTotal = totalAtivos, limite = 20),
            BlocoPlan("Situação hoje", "Situação", PlanKit.contagem(ativosHoje) { info.getValue(it.id).situacao },
                { ativoE("$sitEf=${PlanKit.texto(it)}") }, SharedExcelChartType.PIE),
            BlocoPlan("Efetivo ativo por função", "Função", PlanKit.contagem(ativosHoje) { it.funcaoOuSem },
                { ativoE("$funcEf=${PlanKit.texto(it)}") }, formulaTotal = totalAtivos, limite = 15),
            BlocoPlan("Efetivo ativo por vínculo", "Vínculo", PlanKit.contagem(ativosHoje) { it.vinculo },
                { ativoE("${efetivo.faixa("VÍNCULO")}=${PlanKit.texto(it)}") }, SharedExcelChartType.PIE),
            BlocoPlan("Controle de folgas", "Status", PlanKit.contagem(ativosHoje) { rotuloFolga(info.getValue(it.id).folga?.status.orEmpty()) },
                { efetivo.contarSe("STATUS DA FOLGA", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Transferências por status", "Status", PlanKit.contagem(d.transferencias) { rotuloStatus(it.status) },
                { transf.contarSe("STATUS", it) }, SharedExcelChartType.PIE),
            BlocoPlan("Dias lançados por tipo (12 meses)", "Tipo", PlanKit.contagem(lancs) { it.tipo },
                { dias.contarSe("TIPO", it) }),
            BlocoPlan("Dias lançados por mês", "Mês", PlanKit.contagem(lancs) { it.data.take(7) }.sortedBy { it.first },
                { dias.contarSe("MÊS", it) }, limite = 13)
        )
        val dash = PlanKit.dashboard(
            "DASHBOARD", "Dashboard · Efetivo em ${Datas.br(hoje)}",
            "Filtre as abas EFETIVO, TRANSFERÊNCIAS ou DIAS LANÇADOS e o dashboard acompanha. Gerado em ${PlanData.geradoEm()}.",
            kpis, blocos
        )
        return PlanKit.gerar(listOf(dash, efetivo.sheet, obras.sheet, obraFuncao.sheet, transf.sheet, dias.sheet))
    }
}
